/**
 * Games — unified browse-and-join screen.
 *
 * Public lobbies with room, excluding joined games and pending invitations.
 * Those games are already available in the player's home list.
 *
 * Infinite scroll (cursor-paginated).
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { getPublicOpenGames } from '../../../lib/publicLobbySummary'
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native'
import { useRouter, useFocusEffect } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useLobbyGames } from '../../../hooks/useGames'
import { useAuth } from '../../../hooks/useAuth'
import { usePullToRefresh } from '../../../hooks/usePullToRefresh'
import { GameRow, GameSkeletonRow } from '../../../components/GameRow'
import { Button } from '../../../components/ui/Button'
import { AppModal } from '../../../components/ui/AppModal'
import { JoinByCodeView } from '../../../components/game/lobby/JoinByCodeView'
import { goBackOrHome } from '../../../lib/goBack'
import type { GameResponse } from '@warcarp/api-types'

export default function GamesScreen() {
  const router = useRouter()
  const { user } = useAuth()
  const isWide = useWindowDimensions().width >= 720

  // "Join private game" by code. Private games aren't in the public list, so
  // the viewer opens the Join a game sheet, types the code, then picks a seat
  // in the same sheet (components/game/lobby/JoinByCodeView; invite links use
  // the same view at /(app)/join/<code>).
  const [joinSheetOpen, setJoinSheetOpen] = React.useState(false)
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useLobbyGames()
  const pull = usePullToRefresh(refetch)

  // Refetch on every screen focus so navigating back here (e.g. from
  // a 404 stub, or after deleting a game) always shows the current
  // list — React Query's default 30 s staleTime would otherwise serve
  // a cached page. The empty deps array is intentional: refetch is
  // stable in React Query 5, and we want to fire once per focus event.
  useFocusEffect(
    React.useCallback(() => {
      void refetch()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  )

  const games: GameResponse[] = React.useMemo(() => {
    const flat = data?.pages.flatMap((page) => page.games) ?? []
    return getPublicOpenGames(flat, user?.id)
  }, [data, user?.id])
  const totalOpenGameCount = data?.pages[0]?.publicOpenGameCount
  const openCount = totalOpenGameCount ?? games.length
  const countLabel = `${openCount}${totalOpenGameCount === undefined && hasNextPage ? '+' : ''} open ${openCount === 1 ? 'game' : 'games'}`

  function handleEndReached() {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }

  function handleGamePress(game: GameResponse) {
    // The unified lobby screen handles preview, color choice and joining.
    router.push(`/(app)/games/${game.id}`)
  }
  const joinPrivateButton = <Button title="Join Private Game" size={isWide ? 'sm' : 'md'}
    onPress={() => setJoinSheetOpen(true)}
    accessibilityLabel="Join a private game with an invite code" />

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive gap-3" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="mr-1 p-1"
        >
          <Text className="font-typed text-blood text-base">← Back</Text>
        </TouchableOpacity>
        <Text className="font-stencil text-ink text-lg uppercase flex-1">
          {isLoading ? 'Open games' : countLabel}
        </Text>
        {isWide && joinPrivateButton}
      </View>

      <FlatList
        data={isLoading ? (Array.from({ length: 3 }) as undefined[]) : games}
        keyExtractor={(item, index) =>
          isLoading || !item ? `skeleton-${index}` : (item as GameResponse).id
        }
        renderItem={({ item }) => {
          if (isLoading || !item) return <GameSkeletonRow />
          const game = item as GameResponse
          return (
            <GameRow
              game={game}
              currentUserId={user?.id}
              onPress={() => handleGamePress(game)}
            />
          )
        }}
        contentContainerClassName="px-4 gap-3 pb-8 pt-4"
        refreshControl={
          <RefreshControl
            refreshing={pull.refreshing}
            onRefresh={pull.onRefresh}
            tintColor="#ef4444"
          />
        }
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.3}
        ListFooterComponent={
          isFetchingNextPage ? (
            <View className="py-4 items-center">
              <ActivityIndicator color="#94a3b8" />
            </View>
          ) : null
        }
        ListEmptyComponent={
          !isLoading ? (
            <View className="items-center py-16 px-6 gap-4">
              <Text className="text-ink-soft text-base font-typed text-center">
                No open public games right now.
              </Text>
            </View>
          ) : null
        }
      />

      {/* Pinned footer: join a private game by code (private games aren't in
          the public list above). */}
      {!isWide && <View className="border-t border-olive bg-canvas px-4 py-3">{joinPrivateButton}</View>}

      <AppModal visible={joinSheetOpen} animationType="slide" transparent={false} onRequestClose={() => setJoinSheetOpen(false)}>
        {joinSheetOpen && <JoinByCodeView onClose={() => setJoinSheetOpen(false)} />}
      </AppModal>

    </SafeAreaView>
  )
}
